import { getStackZIndex, useModalStackDepth } from '@/contexts/ModalStackContext';
import * as Dialog from '@radix-ui/react-dialog';
import { Icon } from '@/components/ui/Icon';
import { ICONS } from '@/constants/icons';
import { cn } from '@/utils/cn';
import type { HelpVideoProvider, HelpVideoType } from '@/types/help';

/**
 * VideoEmbedModal Component
 *
 * A modal dialog for playing YouTube / Vimeo embeds via iframe.
 * Separate from VideoPlayerModal (which handles HTML5 <video> for direct file URLs).
 *
 * - Dark cinema-style background (same look as VideoPlayerModal)
 * - 16:9 aspect ratio iframe — adapts to any modal width
 * - Click outside or Escape to close
 * - autoplay passed in embed URL so playback starts immediately
 */

interface VideoEmbedModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback when modal should close */
  onClose: () => void;
  /** Platform */
  provider: HelpVideoProvider;
  /** Whether it's an individual video or a playlist/showcase */
  type: HelpVideoType;
  /** Platform-native video ID (or playlist/showcase ID) */
  videoId: string;
  /** Full original URL — used to extract Vimeo privacy hash for unlisted videos */
  videoUrl?: string | null;
  /** Optional title shown above the player */
  title?: string;
  /** Optional description shown below the title */
  description?: string | null;
}

function buildEmbedUrl(
  provider: HelpVideoProvider,
  type: HelpVideoType,
  videoId: string,
  videoUrl?: string | null
): string {
  if (provider === 'YOUTUBE') {
    if (type === 'PLAYLIST') {
      return `https://www.youtube.com/embed/videoseries?list=${videoId}&autoplay=1`;
    }
    return `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`;
  }

  // VIMEO
  if (type === 'PLAYLIST') {
    return `https://vimeo.com/showcase/${videoId}/embed`;
  }

  // Vimeo single video — unlisted videos require the privacy hash (?h=...).
  // Extract from path form `vimeo.com/<id>/<hash>` or query form `?h=<hash>`.
  const hashMatch =
    videoUrl?.match(/vimeo\.com\/\d+\/([A-Za-z0-9]+)/) ??
    videoUrl?.match(/[?&]h=([A-Za-z0-9]+)/);
  const hashParam = hashMatch ? `&h=${hashMatch[1]}` : '';
  return `https://player.vimeo.com/video/${videoId}?autoplay=1&title=0&byline=0&portrait=0${hashParam}`;
}

export const VideoEmbedModal = ({
  isOpen,
  onClose,
  provider,
  type,
  videoId,
  videoUrl,
  title,
  description,
}: VideoEmbedModalProps) => {
  const embedUrl = buildEmbedUrl(provider, type, videoId, videoUrl);
  const stackZ = getStackZIndex(useModalStackDepth(isOpen));

  return (
    <Dialog.Root open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        {/* Backdrop — same z-index layer as VideoPlayerModal */}
        <Dialog.Overlay
          style={{ zIndex: stackZ.backdrop }}
          className={cn(
            'fixed inset-0 bg-black/80 backdrop-blur-sm',
            'flex items-center justify-center',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0'
          )}
        >
          {/* Modal panel */}
          <Dialog.Content
            className={cn(
              // Size — same width cap as VideoPlayerModal; height comes from inner aspect-video
              'w-[95vw] max-w-4xl',

              // Dark cinema feel
              'bg-gray-900 rounded-2xl shadow-xl',

              // Stacking
              'z-[101]',

              // Layout
              'relative',

              // Animations
              'focus:outline-none',
              'data-[state=open]:animate-in data-[state=closed]:animate-out',
              'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
              'duration-150'
            )}
          >
            {/* Accessible title */}
            {title ? (
              <div className="px-6 pt-6 pb-3">
                <Dialog.Title className="text-lg font-semibold text-white truncate pr-10">
                  {title}
                </Dialog.Title>
                {description && (
                  <p className="text-sm text-gray-300 mt-1 line-clamp-3 pr-10">
                    {description}
                  </p>
                )}
              </div>
            ) : (
              // eslint-disable-next-line i18next/no-literal-string
              <Dialog.Title className="sr-only">Video Player</Dialog.Title>
            )}

            {/* Accessible description */}
            <Dialog.Description className="sr-only">
              {/* eslint-disable-next-line i18next/no-literal-string */}
              {title ? `Playing video: ${title}` : 'Video player dialog'}
            </Dialog.Description>

            {/* Close button */}
            <Dialog.Close
              className={cn(
                'absolute top-4 right-4 z-10',
                'w-10 h-10 rounded-full',
                'flex items-center justify-center',
                'bg-black/50 hover:bg-black/70',
                'text-white hover:text-gray-200',
                'transition-all duration-200',
                'focus:outline-none'
              )}
              aria-label="Close video player"
            >
              <Icon name={ICONS.CLOSE} size="sm" />
            </Dialog.Close>

            {/* iframe container — 16:9 via aspect-video */}
            <div className={cn('px-6 pb-6', !title && 'pt-6')}>
              <div className="relative aspect-video w-full rounded-lg overflow-hidden bg-black">
                <iframe
                  src={embedUrl}
                  title={title ?? 'Video player'}
                  allow="autoplay; fullscreen; picture-in-picture; encrypted-media"
                  allowFullScreen
                  className="absolute inset-0 w-full h-full"
                />
              </div>
            </div>
          </Dialog.Content>
        </Dialog.Overlay>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
